import type { DailyScoreLedger, IsoDate } from '../models/types';
import { addDays } from '../utils/date';

/**
 * Detaylı Geçmiş grafiği — CLAUDE.md §8'den çıkarılıp kapsama alındı.
 *
 * Bu dosya SAF fonksiyonlardan oluşur: veritabanı, DOM ve saat okumaz;
 * kendisine verilen kayıtlardan bir SVG metni üretir. Sebep
 * `scoring.ts` ve `schedule.ts` ile aynı: "şu veriyle grafik nasıl
 * görünür?" sorusu, ekranı açmadan ve tarih beklemeden yanıtlanabilir
 * olsun.
 *
 * NEDEN KÜTÜPHANE YOK: Chart.js/D3 gibi bir paket, pakete onlarca kB
 * ekler ve kendi renk/tipografi dünyasını getirir; her şeyi §5'teki
 * token'lara geri çevirmek, elle çizmekten uzun sürerdi. Burada
 * çizilen iki grafik de birkaç düz çizgi ve dikdörtgenden ibaret.
 */

/* ==================================================================
   Türler
   ================================================================== */

/** Grafikte hangi veri çiziliyor. */
export type ChartMetric = 'bar' | 'delta';

/**
 * Hazır tarih aralıkları + kullanıcının kendi seçtiği aralık.
 * Sayılar gün cinsinden; `all` tüm kayıtlar, `custom` iki tarih alanı.
 */
export type RangeKey = '30' | '90' | '365' | 'all' | 'custom';

export interface DateRange {
  start: IsoDate;
  end: IsoDate;
}

/* ==================================================================
   Aralık hesabı
   ================================================================== */

/** Hazır aralıkların etiketleri — seçici şeridinde bu sırayla çıkar. */
export const RANGE_OPTIONS: readonly { key: RangeKey; label: string }[] = [
  { key: '30', label: 'Son 30 gün' },
  { key: '90', label: 'Son 90 gün' },
  { key: '365', label: 'Son 1 yıl' },
  { key: 'all', label: 'Tümü' },
  { key: 'custom', label: 'Özel' },
];

/**
 * Seçilen aralığı gerçek iki tarihe çevirir.
 *
 * `entries` boşsa `null` döner — çizecek bir şey yok demektir.
 * Hazır aralıklar BUGÜNDEN geriye sayılır; "son 30 gün" ifadesinin
 * beklenen anlamı bu. Kayıtların ilk gününden öncesine düşerse
 * kırpılmıyor: grafiğin boş kalan sol tarafı, uygulamanın o tarihte
 * henüz kullanılmadığını dürüstçe gösterir.
 */
export function resolveRange(
  key: RangeKey,
  custom: { start: IsoDate | null; end: IsoDate | null },
  entries: readonly DailyScoreLedger[],
  today: IsoDate,
): DateRange | null {
  const first = entries[0]?.date;
  const last = entries[entries.length - 1]?.date;
  if (!first || !last) return null;

  if (key === 'custom') {
    // Kullanıcı henüz iki tarihi de doldurmadıysa tüm aralığa düşüyoruz;
    // ekran boş bir grafikle karşılamasın.
    if (!custom.start || !custom.end) return { start: first, end: last };
    // Ters girilmiş aralığı reddetmek yerine düzeltiyoruz: kullanıcı ne
    // demek istediği belli, hata mesajı göstermek gereksiz sürtünme.
    return custom.start <= custom.end
      ? { start: custom.start, end: custom.end }
      : { start: custom.end, end: custom.start };
  }

  if (key === 'all') return { start: first, end: last };

  // `-(n - 1)`: "son 30 gün" bugünü de kapsayan 30 günlük penceredir.
  return { start: addDays(today, -(Number(key) - 1)), end: today };
}

/** Aralığa düşen kayıtları süzer (tarihler `yyyy-mm-dd`, metin olarak karşılaştırılabilir). */
export function filterByRange(
  entries: readonly DailyScoreLedger[],
  range: DateRange,
): DailyScoreLedger[] {
  return entries.filter((entry) => entry.date >= range.start && entry.date <= range.end);
}

/* ==================================================================
   Gruplama (yalnızca günlük değişim grafiği için)
   ================================================================== */

/** Bir sütunun kapsadığı dönem ve o dönemin net puan değişimi. */
interface Bucket {
  label: string;
  net: number;
}

/** Sütunların neyi temsil ettiği — kullanıcıya yazıyla da söyleniyor. */
export type BucketMode = 'daily' | 'weekly' | 'monthly';

const SHORT_MONTHS = [
  'Oca',
  'Şub',
  'Mar',
  'Nis',
  'May',
  'Haz',
  'Tem',
  'Ağu',
  'Eyl',
  'Eki',
  'Kas',
  'Ara',
];

/** `2026-09-11` → `11 Eyl`. */
function shortDate(date: IsoDate): string {
  const month = SHORT_MONTHS[Number(date.slice(5, 7)) - 1] ?? '';
  return `${Number(date.slice(8, 10))} ${month}`;
}

/** `2026-09-11` → `Eyl 26`. */
function shortMonth(date: IsoDate): string {
  const month = SHORT_MONTHS[Number(date.slice(5, 7)) - 1] ?? '';
  return `${month} ${date.slice(2, 4)}`;
}

/**
 * Sütun sayısı ekrana sığacak şekilde gruplama sıklığını seçer.
 *
 * 730 günü tek tek çizmek, telefon genişliğinde her sütuna yarım
 * pikselden az düşürürdü — grafik gri bir bulanıklığa dönerdi. Bu
 * yüzden aralık uzadıkça günler önce haftaya, sonra aya toplanıyor.
 */
export function pickBucketMode(dayCount: number): BucketMode {
  if (dayCount <= 45) return 'daily';
  if (dayCount <= 200) return 'weekly';
  return 'monthly';
}

/** Sütunların ne anlama geldiğini söyleyen Türkçe etiket. */
export function describeBucketMode(mode: BucketMode): string {
  if (mode === 'daily') return 'günlük';
  if (mode === 'weekly') return 'haftalık';
  return 'aylık';
}

/**
 * Kayıtları sütunlara toplar.
 *
 * Haftalık modda haftalar takvim haftası değil, ARALIĞIN BAŞINDAN
 * itibaren 7'şerlik dilimlerdir: kullanıcı "son 90 gün" dediğinde
 * pencerenin kendi başlangıcı referans olsun, ilk sütun yarım hafta
 * olarak görünmesin diye.
 */
function bucketEntries(
  entries: readonly DailyScoreLedger[],
  mode: BucketMode,
  range: DateRange,
): Bucket[] {
  if (mode === 'daily') {
    return entries.map((entry) => ({ label: shortDate(entry.date), net: entry.finalDelta }));
  }

  const groups = new Map<string, Bucket>();

  for (const entry of entries) {
    let key: string;
    let label: string;

    if (mode === 'monthly') {
      key = entry.date.slice(0, 7);
      label = shortMonth(entry.date);
    } else {
      // Aralığın başlangıcından bu güne kaç gün geçti → kaçıncı hafta.
      const dayOffset = Math.floor(
        (Date.parse(`${entry.date}T12:00:00Z`) - Date.parse(`${range.start}T12:00:00Z`)) /
          86_400_000,
      );
      const week = Math.floor(dayOffset / 7);
      key = String(week);
      label = shortDate(addDays(range.start, week * 7));
    }

    const existing = groups.get(key);
    if (existing) existing.net += entry.finalDelta;
    else groups.set(key, { label, net: entry.finalDelta });
  }

  return [...groups.values()];
}

/* ==================================================================
   SVG geometrisi

   Sabit bir viewBox kullanıp SVG'yi `width: 100%` ile esnetiyoruz.
   Böylece tüm ölçüler (yazı boyutu, çizgi kalınlığı) tek bir
   koordinat sisteminde kalıyor ve ekran genişliğine göre orantılı
   büyüyor. 340 birim, iPhone'da yaklaşık 340 piksele denk gelir —
   yani buradaki "10 birim" gerçekte ~10 piksellik yazıdır.
   ================================================================== */

const VIEW_W = 340;
const VIEW_H = 180;
const PAD_LEFT = 34;
const PAD_RIGHT = 6;
const PAD_TOP = 12;
const PAD_BOTTOM = 24;

/** Çizimin fiilen yapıldığı alan (eksen boşlukları düşülmüş hâli). */
const PLOT_W = VIEW_W - PAD_LEFT - PAD_RIGHT;
const PLOT_H = VIEW_H - PAD_TOP - PAD_BOTTOM;

/** Sayıyı SVG'ye yazarken tek ondalığa kırpar (dosya boyu şişmesin). */
function n(value: number): string {
  return Number.isInteger(value) ? String(value) : value.toFixed(1);
}

/** Eksen etiketi. */
function axisLabel(x: number, y: number, text: string, anchor: string): string {
  return `<text x="${n(x)}" y="${n(y)}" class="chart__label" text-anchor="${anchor}">${text}</text>`;
}

/**
 * X ekseninde en fazla üç tarih etiketi gösterir (baş, orta, son).
 *
 * Daha fazlası telefon genişliğinde üst üste biner. Kullanıcının
 * grafikten beklediği "nereden nereye" bilgisi için üçü yetiyor.
 */
function renderXLabels(labels: readonly string[]): string {
  if (labels.length === 0) return '';

  const baseY = VIEW_H - PAD_BOTTOM + 14;
  const positions = labels.length === 1 ? [0] : [0, Math.floor((labels.length - 1) / 2), labels.length - 1];
  const unique = [...new Set(positions)];

  return unique
    .map((index, order) => {
      const x = labels.length === 1 ? PAD_LEFT + PLOT_W / 2 : PAD_LEFT + (index / (labels.length - 1)) * PLOT_W;
      // Baştaki etiket sola, sondaki sağa taşmasın diye hizalamayı
      // uçlarda değiştiriyoruz.
      const anchor = order === 0 ? 'start' : order === unique.length - 1 ? 'end' : 'middle';
      return axisLabel(x, baseY, labels[index] ?? '', anchor);
    })
    .join('');
}

/** Boş durum — aralıkta hiç kayıt yoksa. */
function renderEmptyChart(): string {
  return `
    <div class="chart__empty">
      <p class="chart__empty-title">Bu aralıkta kayıt yok</p>
      <p class="chart__empty-hint">Başka bir tarih aralığı seç.</p>
    </div>
  `;
}

/* ==================================================================
   Başarı Barı çizgisi
   ================================================================== */

/**
 * Y ekseninin alt ve üst sınırını seçer.
 *
 * Sabit 0–1000 kullanmıyoruz: bar uzun süredir 900'lerdeyse çizgi
 * grafiğin tepesine yapışır ve hiçbir şey okunmaz. Bunun yerine
 * veriye yaklaşıyoruz — ama en az 100 puanlık bir pencere
 * bırakarak. Bu alt sınır olmasaydı 3 puanlık bir dalgalanma ekranı
 * dolduran bir dağ silsilesi gibi görünür, kullanıcı olmayan bir
 * çöküşe bakardı.
 */
function resolveScale(values: readonly number[]): { lo: number; hi: number } {
  const MIN_SPAN = 100;
  const dataMin = Math.min(...values);
  const dataMax = Math.max(...values);

  let lo = Math.max(0, Math.floor(dataMin - 15));
  let hi = Math.min(1000, Math.ceil(dataMax + 15));

  if (hi - lo < MIN_SPAN) {
    // Önce yukarı doğru genişlet; tavana dayanırsak aşağı taşıyoruz.
    hi = Math.min(1000, lo + MIN_SPAN);
    if (hi - lo < MIN_SPAN) lo = Math.max(0, hi - MIN_SPAN);
  }

  return { lo, hi };
}

function renderBarChart(entries: readonly DailyScoreLedger[]): string {
  const values = entries.map((entry) => entry.barValueAfter);
  const { lo, hi } = resolveScale(values);

  const baseY = PAD_TOP + PLOT_H;

  const xOf = (index: number): number =>
    values.length === 1
      ? PAD_LEFT + PLOT_W / 2
      : PAD_LEFT + (index / (values.length - 1)) * PLOT_W;
  const yOf = (value: number): number => PAD_TOP + (1 - (value - lo) / (hi - lo)) * PLOT_H;

  const points = values.map((value, index) => `${n(xOf(index))},${n(yOf(value))}`);

  // Çizginin altını dolduran alan: tek başına çizgi, ince bir tel gibi
  // durur; dolgu grafiğe ağırlık ve yön hissi veriyor.
  const areaPath = `M${points[0] ?? ''} L${points.join(' L')} L${n(xOf(values.length - 1))},${n(baseY)} L${n(xOf(0))},${n(baseY)} Z`;
  const linePath = `M${points.join(' L')}`;

  // Üç yatay kılavuz: alt, orta, üst.
  const mid = Math.round((lo + hi) / 2);
  const guides = [hi, mid, lo]
    .map((value) => {
      const y = yOf(value);
      return `
        <line x1="${PAD_LEFT}" y1="${n(y)}" x2="${PAD_LEFT + PLOT_W}" y2="${n(y)}" class="chart__grid" />
        ${axisLabel(PAD_LEFT - 6, y + 3, String(value), 'end')}
      `;
    })
    .join('');

  const last = values[values.length - 1];

  return `
    <svg class="chart__svg" viewBox="0 0 ${VIEW_W} ${VIEW_H}" role="img"
         aria-label="Başarı Barı grafiği: ${entries[0]?.date ?? ''} ile ${entries[entries.length - 1]?.date ?? ''} arası, son değer ${last ?? 0}">
      <defs>
        <linearGradient id="chartArea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="var(--color-success-start)" stop-opacity="0.28" />
          <stop offset="100%" stop-color="var(--color-success-start)" stop-opacity="0.02" />
        </linearGradient>
      </defs>
      ${guides}
      <path d="${areaPath}" fill="url(#chartArea)" />
      <path d="${linePath}" class="chart__line" />
      ${
        // Tek kayıt varsa çizgi görünmez; noktayı ayrıca işaretliyoruz.
        values.length === 1
          ? `<circle cx="${n(xOf(0))}" cy="${n(yOf(values[0] ?? 0))}" r="3" class="chart__dot" />`
          : `<circle cx="${n(xOf(values.length - 1))}" cy="${n(yOf(last ?? 0))}" r="3" class="chart__dot" />`
      }
      ${renderXLabels(entries.map((entry) => shortDate(entry.date)))}
    </svg>
  `;
}

/* ==================================================================
   Günlük değişim sütunları
   ================================================================== */

function renderDeltaChart(entries: readonly DailyScoreLedger[], range: DateRange, mode: BucketMode): string {
  const buckets = bucketEntries(entries, mode, range);
  const nets = buckets.map((bucket) => bucket.net);

  // Sıfır çizgisi her zaman görünür olmalı: pozitif ve negatif
  // sütunların hangi tarafta olduğu ancak ona göre okunur.
  let lo = Math.min(0, ...nets);
  let hi = Math.max(0, ...nets);
  if (lo === hi) {
    // Tüm sütunlar sıfırsa bölme hatası olmasın diye yapay bir pencere.
    lo = -1;
    hi = 1;
  }


  const yOf = (value: number): number => PAD_TOP + (1 - (value - lo) / (hi - lo)) * PLOT_H;
  const zeroY = yOf(0);

  const slot = PLOT_W / buckets.length;
  // Sütun genişliği: yuvanın %72'si, ama hiçbir zaman görünmez kadar
  // ince değil — 700 günlük aralıkta bile en az 1 birim kalsın.
  const barW = Math.max(1, slot * 0.72);

  const bars = buckets
    .map((bucket, index) => {
      const x = PAD_LEFT + index * slot + (slot - barW) / 2;
      const valueY = yOf(bucket.net);
      const top = Math.min(valueY, zeroY);
      // Sıfıra çok yakın sütunlar tamamen kaybolmasın diye taban 1 birim.
      const height = Math.max(1, Math.abs(valueY - zeroY));
      const tone = bucket.net >= 0 ? 'chart__bar--up' : 'chart__bar--down';
      return `<rect x="${n(x)}" y="${n(top)}" width="${n(barW)}" height="${n(height)}"
                    rx="${n(Math.min(barW / 2, 2))}" class="chart__bar ${tone}" />`;
    })
    .join('');

  return `
    <svg class="chart__svg" viewBox="0 0 ${VIEW_W} ${VIEW_H}" role="img"
         aria-label="${describeBucketMode(mode)} puan değişimi grafiği, ${buckets.length} sütun">
      <line x1="${PAD_LEFT}" y1="${n(yOf(hi))}" x2="${PAD_LEFT + PLOT_W}" y2="${n(yOf(hi))}" class="chart__grid" />
      ${axisLabel(PAD_LEFT - 6, yOf(hi) + 3, `+${Math.round(hi)}`, 'end')}
      ${bars}
      <line x1="${PAD_LEFT}" y1="${n(zeroY)}" x2="${PAD_LEFT + PLOT_W}" y2="${n(zeroY)}" class="chart__zero" />
      ${axisLabel(PAD_LEFT - 6, zeroY + 3, '0', 'end')}
      ${
        lo < 0
          ? `<line x1="${PAD_LEFT}" y1="${n(yOf(lo))}" x2="${PAD_LEFT + PLOT_W}" y2="${n(yOf(lo))}" class="chart__grid" />
             ${axisLabel(PAD_LEFT - 6, yOf(lo) + 3, String(Math.round(lo)), 'end')}`
          : ''
      }
      ${renderXLabels(buckets.map((bucket) => bucket.label))}
    </svg>
  `;
}

/* ==================================================================
   Dışarıya açılan tek çizim fonksiyonu
   ================================================================== */

export interface ChartResult {
  svg: string;
  /** Grafiğin altında duran özet satırı (metriğe göre değişir). */
  summary: string;
}

/**
 * Seçilen metrik ve aralık için grafiği ve özet satırını üretir.
 * Kayıt yoksa boş durum HTML'i döner.
 */
export function renderChart(
  entries: readonly DailyScoreLedger[],
  metric: ChartMetric,
  range: DateRange,
): ChartResult {
  if (entries.length === 0) {
    return { svg: renderEmptyChart(), summary: '' };
  }

  if (metric === 'bar') {
    const values = entries.map((entry) => entry.barValueAfter);
    const first = values[0] ?? 0;
    const last = values[values.length - 1] ?? 0;
    const change = last - first;
    return {
      svg: renderBarChart(entries),
      summary: [
        `${entries.length} gün`,
        `en düşük ${Math.min(...values)}`,
        `en yüksek ${Math.max(...values)}`,
        `dönem değişimi ${change >= 0 ? '+' : ''}${change}`,
      ].join(' · '),
    };
  }

  const dayCount = entries.length;
  const mode = pickBucketMode(dayCount);
  const deltas = entries.map((entry) => entry.finalDelta);
  const total = deltas.reduce((sum, value) => sum + value, 0);

  return {
    svg: renderDeltaChart(entries, range, mode),
    summary: [
      `${describeBucketMode(mode)} sütunlar`,
      `toplam ${total >= 0 ? '+' : ''}${total}`,
      `en iyi gün +${Math.max(...deltas)}`,
      `en kötü gün ${Math.min(...deltas)}`,
    ].join(' · '),
  };
}
